import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Home() {
  const BOOKING_URL = "https://link.nordiccrm.ca/widget/bookings/dorman-ernest-baltazar-personal-calendar-qg2bbc8wa";

  return (
    <div className="w-full bg-kin-black">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[90vw] h-[70vh] bg-kin-gold/5 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center flex flex-col items-center">
          <FadeUp delay={0.1}>
            <p className="text-kin-gold tracking-[0.3em] text-[10px] md:text-sm uppercase font-light mb-6 md:mb-10">
              Purpose-Driven Creator Development
            </p>
          </FadeUp>
          
          <FadeUp delay={0.3}>
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-serif text-balance leading-[1.2] md:leading-[1.1] text-kin-ivory mb-8 md:mb-12">
              Build A Life, Brand, And Creative Ecosystem <br className="hidden md:block" />
              <span className="italic text-kin-ivory/70 block sm:inline mt-2 sm:mt-0">Without Losing Yourself.</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.5}>
            <p className="max-w-2xl mx-auto text-sm md:text-lg text-kin-ivory/50 font-light leading-loose tracking-wide mb-10 md:mb-16 px-4">
              Kingdom Inspira Nova Studio helps creators, entrepreneurs, and visionaries build sustainable systems rooted in identity, stewardship, and long-term impact.
            </p>
          </FadeUp>

          <FadeUp delay={0.7} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full px-4">
            <Link to="/blueprint" className="w-full sm:w-auto">
              <Button variant="primary" className="w-full sm:px-12">Explore The Blueprint</Button>
            </Link>
            <Link to="/clarity-guide" className="w-full sm:w-auto">
              <Button variant="secondary" className="w-full sm:px-12">Start With The Free Clarity Guide</Button>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* MANIFESTO SECTION */}
      <section className="py-24 md:py-48 px-6 bg-[#09090b]">
        <div className="max-w-4xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-2xl md:text-5xl lg:text-6xl font-serif text-kin-ivory leading-snug mb-12 md:mb-20 text-balance">
              Most People Are Building Lives They <span className="italic text-kin-gold">Secretly Don’t Want.</span>
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.2}>
            <div className="space-y-6 md:space-y-10 text-base md:text-xl text-kin-ivory/60 font-light leading-relaxed md:leading-loose tracking-wide text-left md:text-center max-w-3xl mx-auto">
              <p>Modern hustle culture taught creators to optimize for visibility, speed, and productivity.</p>
              <p className="text-kin-ivory font-normal">But very few people stop long enough to ask:</p>
              <p className="font-serif text-xl md:text-3xl text-kin-gold italic py-4 md:py-8 border-y border-kin-ivory/5">
                “Is this life actually aligned with who I am becoming?”
              </p>
              <p>Through aligned systems, emotional sustainability, and purpose-driven architecture, we help visionaries create work that lasts without sacrificing peace.</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ECOSYSTEM GRID */}
      <section className="py-24 md:py-48 px-6 border-t border-kin-ivory/5">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="text-[10px] md:text-sm font-sans tracking-[0.4em] uppercase text-kin-gold text-center mb-16 md:mb-32">
              The Ecosystem
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              { title: "Clarity Guide", link: "/clarity-guide", desc: "A reflective framework for internal alignment." },
              { title: "Kingdom Blueprint", link: "/blueprint", desc: "Flagship systems-based framework for sustainable creators." },
              { title: "THRIVE Wellness", link: "/thrive", desc: "A wholistic guide to protect your energy and longevity." }
            ].map((item, i) => (
              <FadeUp key={i} delay={i * 0.15} className="flex flex-col group">
                <div className="h-[1px] w-full bg-kin-ivory/10 mb-8 md:mb-10 group-hover:bg-kin-gold/40 transition-colors duration-500"></div>
                <h3 className="text-xl md:text-3xl font-serif text-kin-ivory mb-4 md:mb-6 leading-tight group-hover:text-kin-gold transition-colors">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-kin-ivory/40 font-light leading-relaxed mb-8 md:mb-12 flex-grow">
                  {item.desc}
                </p>
                <Link to={item.link} className="text-kin-gold text-[10px] md:text-xs tracking-[0.2em] uppercase flex items-center gap-3 w-fit group-hover:gap-5 transition-all">
                  Access Now
                  <span className="w-6 h-[1px] bg-kin-gold/40 group-hover:w-10 group-hover:bg-kin-gold transition-all"></span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION */}
      <section className="py-20 md:py-32 px-6 bg-[#09090b] overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 md:gap-24">
          <FadeUp className="w-full lg:w-1/2 relative group">
            <div className="aspect-[4/5] sm:aspect-square lg:aspect-[4/5] w-full overflow-hidden relative">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/9c5adfa6bba615730f4558013336717062b18ab46d11ed47c31c88c932e61ffbf11018f89f2243f6aef891f7c9f9eda4/images/aW1hZ2UvanBlZw==/e3a999aa4bef11412c7c81e9adfb1276.jpeg" 
                alt="Dorman Baltazar" 
                className="object-cover w-full h-full opacity-60 grayscale mix-blend-luminosity transition-all duration-1000 group-hover:scale-105 group-hover:opacity-80 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent"></div>
            </div>
            <div className="absolute -bottom-4 -left-4 md:-bottom-8 md:-left-8 w-24 h-24 md:w-40 md:h-40 border-b border-l border-kin-gold/20 -z-10 group-hover:border-kin-gold/40 transition-colors"></div>
          </FadeUp>
          
          <div className="w-full lg:w-1/2 space-y-8 md:space-y-12">
            <FadeUp delay={0.2}>
              <h2 className="text-[10px] md:text-xs font-sans tracking-[0.4em] uppercase text-kin-gold mb-4 md:mb-6">
                The Architect
              </h2>
              <p className="text-2xl md:text-5xl font-serif text-kin-ivory leading-tight mb-6 md:mb-10 text-balance">
                Building lives that remain whole under pressure.
              </p>
            </FadeUp>
            
            <FadeUp delay={0.3}>
              <div className="space-y-6 text-sm md:text-lg text-kin-ivory/50 font-light leading-relaxed tracking-wide">
                <p>Kingdom Inspira Nova Studio was created to help creators build lives and businesses that remain whole under pressure.</p>
                <p className="text-kin-ivory/80 italic font-serif text-lg md:text-2xl border-l-[1px] border-kin-gold/50 pl-6 py-2 my-6">
                  Not through hustle.<br className="md:hidden"/> Performance.<br className="md:hidden"/> Or burnout.
                </p>
                <p>But through clarity, stewardship, and aligned creation.</p>
              </div>
            </FadeUp>

            <FadeUp delay={0.4} className="pt-4 flex flex-col sm:flex-row gap-4">
              <Link to="/about" className="w-full sm:w-auto">
                <Button variant="secondary" className="w-full">Read The Story</Button>
              </Link>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button variant="ghost" className="w-full border border-kin-ivory/10">Schedule A Discovery Call</Button>
              </a>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 md:py-60 px-6 text-center relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] h-[80vh] bg-kin-gold/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-2xl md:text-6xl lg:text-7xl font-serif text-kin-ivory leading-snug mb-12 md:mb-20 text-balance px-4">
              You Do Not Need To Build Faster.<br/>
              <span className="italic text-kin-gold">You Need To Build Sustainably.</span>
            </h2>
          </FadeUp>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
            <FadeUp delay={0.2} className="w-full sm:w-auto">
              <Link to="/blueprint" className="w-full sm:w-auto">
                <Button variant="primary" className="w-full sm:px-16 py-5">
                  Explore The Blueprint
                </Button>
              </Link>
            </FadeUp>
            <FadeUp delay={0.3} className="w-full sm:w-auto">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button variant="secondary" className="w-full sm:px-16 py-5">
                  Schedule A Discovery Call
                </Button>
              </a>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}